import { useContext, useMemo } from "react";

import {
  StripeRecurringInterval,
  MembershipValidityPeriod,
  getCurrencyFromCountry,
  formatDateToShortDate,
  getField,
  formatSmartNarrowCurrencyFromCents,
  formatDateToMedDate,
} from "@simplyk/common";
import { Control, useWatch } from "react-hook-form";

import { useFrontendAuctionContext } from "../../../contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../contexts/FrontendFormContext";
import { FrontendTicketingContext } from "../../../contexts/FrontendTicketingContext";
import { useLocaleContext } from "../../../contexts/LocaleContext";
import { useTranslate } from "../../../hooks/useTranslate";

import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { TicketingPaymentInput } from "@/types/ticketing";

interface DescriptionData {
  description: string;
  price: number;
  formattedPrice: string;
  isNew: boolean;
  recurrenceExpirationInfo: string;
}

export const useTicketsDescriptions = ({ control }: { control: Control<TicketingPaymentInput> }) => {
  const { t } = useTranslate();
  let ticketsPurchased = useWatch({ control, name: "ticketsPurchased" });
  const { ticketing, isAuction, membershipRenewalBuyerInfo } = useContext(FrontendTicketingContext);
  const { commandBidsRateIdsMap, hasAlreadyBid, highestBidsMapByRateId } = useFrontendAuctionContext();
  const { organization, stripeRecurrenceInterval } = useContext(FrontendFormContext);
  const { isPreview } = usePreviewContext();
  const { locale, isoLocale } = useLocaleContext();
  const currency = getCurrencyFromCountry(organization.country);

  if (isPreview) {
    ticketsPurchased = [
      ...(ticketing.rates?.[0]?.id
        ? [{ rateId: ticketing.rates?.[0]?.id, purchasedNumber: 2, attendeesByTicket: 1 }]
        : []),
    ];
  }
  const displayRecurrenceInterval = useMemo(() => {
    if (!stripeRecurrenceInterval) {
      return "";
    }
    return ` / ${
      stripeRecurrenceInterval === StripeRecurringInterval.year
        ? t("dashboard", "common.year")
        : t("dashboard", "common.month")
    }`;
  }, [stripeRecurrenceInterval, t]);

  const displayRecurrenceExpirationInfo = useMemo(() => {
    const isAtDate = membershipRenewalBuyerInfo?.validityPeriod === MembershipValidityPeriod.AtDate;
    const isOneYearAfterPayment =
      membershipRenewalBuyerInfo?.validityPeriod === MembershipValidityPeriod.OneYearAfterPayment;
    const isOneMonthAfterPayment =
      membershipRenewalBuyerInfo?.validityPeriod === MembershipValidityPeriod.OneMonthAfterPayment;
    const hasNoExpiration = membershipRenewalBuyerInfo?.validityPeriod === MembershipValidityPeriod.NoExpiration;

    if (hasNoExpiration) {
      return "";
    }

    const medExpirationDate = membershipRenewalBuyerInfo?.expirationDate
      ? formatDateToMedDate(membershipRenewalBuyerInfo?.expirationDate, locale)
      : "";
    const shortExpirationDate = membershipRenewalBuyerInfo?.expirationDate
      ? formatDateToShortDate(membershipRenewalBuyerInfo?.expirationDate, locale)
      : "";

    return stripeRecurrenceInterval
      ? isAtDate
        ? t("ticketing", "payment.summary.cancelAnyTimeWithDate", { date: medExpirationDate })
        : isOneYearAfterPayment
          ? t("ticketing", "payment.summary.cancelAnyTimeYearly")
          : isOneMonthAfterPayment
            ? t("ticketing", "payment.summary.cancelAnyTimeMonthly")
            : t("ticketing", "payment.summary.cancelAnyTime")
      : t("ticketing", "payment.summary.expires", { date: shortExpirationDate });
  }, [
    membershipRenewalBuyerInfo?.validityPeriod,
    membershipRenewalBuyerInfo?.expirationDate,
    stripeRecurrenceInterval,
    t,
    locale,
  ]);

  const ticketsDescriptions = useMemo(
    () =>
      ticketsPurchased?.reduce<DescriptionData[]>((prev, current) => {
        const rate = ticketing.rates?.find((rate) => rate.id === current.rateId);

        if (!rate || ((!current.purchasedNumber || current.purchasedNumber <= 0) && !current.bid)) {
          return prev;
        }
        const rateField = getField(rate.rateFields, locale);
        const price = (isAuction ? current.bid : rate.amount * (current.purchasedNumber || 0)) || 0;
        const formattedPrice = `${formatSmartNarrowCurrencyFromCents(price, {
          locale: isoLocale,
          currency,
        })}${displayRecurrenceInterval}`;
        const description = `${current.purchasedNumber}x ${rateField?.title || ""}`;
        const recurrenceExpirationInfo = rate.membershipValidityPeriod ? displayRecurrenceExpirationInfo : "";
        return [...prev, { description, formattedPrice, recurrenceExpirationInfo, isNew: false, price }];
      }, []),
    [
      currency,
      displayRecurrenceExpirationInfo,
      displayRecurrenceInterval,
      isAuction,
      isoLocale,
      locale,
      ticketing.rates,
      ticketsPurchased,
    ]
  );

  const bidsDescriptions = useMemo(() => {
    const newBidsPurchased =
      ticketsPurchased
        ?.filter((ticket) => ticket.bid && ticket.bid > 0)
        ?.map((ticket) => ({ amount: ticket.bid, rateId: ticket.rateId, isNew: true })) || [];

    const oldWinningBidsPurchased = commandBidsRateIdsMap
      ? Object.values(commandBidsRateIdsMap)
          ?.filter((bid) => {
            return (
              !ticketsPurchased?.find((ticket) => ticket.rateId === bid.rateId && ticket.bid && ticket.bid > 0) &&
              highestBidsMapByRateId?.[bid.rateId]?.amount === bid.amount
            );
          })
          .map((bid) => ({ amount: bid.amount, rateId: bid.rateId, isNew: false }))
      : [];
    const allBids = [...newBidsPurchased, ...oldWinningBidsPurchased];

    return allBids.reduce<DescriptionData[]>((prev, current) => {
      const rate = ticketing.rates?.find((rate) => rate.id === current.rateId);
      if (!rate) {
        return prev;
      }
      const rateField = getField(rate.rateFields, locale);
      const price = current.amount || 0;
      const formattedPrice = formatSmartNarrowCurrencyFromCents(price, { locale: isoLocale, currency });
      const description = `${t("ticketing", "auction.payment.bidOn")} ${rateField?.title || ""}`;
      return [
        ...prev,
        { description, price, isNew: hasAlreadyBid && current.isNew, recurrenceExpirationInfo: "", formattedPrice },
      ];
    }, []);
  }, [
    commandBidsRateIdsMap,
    currency,
    hasAlreadyBid,
    highestBidsMapByRateId,
    isoLocale,
    locale,
    t,
    ticketing.rates,
    ticketsPurchased,
  ]);

  return { ticketsDescriptions: isAuction ? bidsDescriptions : ticketsDescriptions };
};
